// Shared Google OAuth Callback Page
// Route: /auth/google/callback
// Handles BOTH Gmail and Calendar OAuth callbacks
// Routing basiert auf dem State-Parameter Prefix: "gmail_" oder "gcal_"

import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar'
import { CheckCircle, XCircle, Loader2, Mail, Calendar } from 'lucide-react'

const REDIRECT_URI = `${window.location.origin}/auth/google/callback`

// Gmail Token Exchange via Vercel API Route
async function exchangeGmailCode(code: string, userId: string): Promise<{ success: boolean; email?: string; error?: string }> {
  try {
    const res = await fetch('/api/gmail-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'exchange',
        code,
        redirect_uri: REDIRECT_URI,
        user_id: userId,
      }),
    })

    const data = await res.json()

    if (!res.ok) {
      return { success: false, error: data.error || `Fehler ${res.status}: ${data.details || ''}` }
    }

    if (data.success) {
      return { success: true, email: data.google_email }
    }

    return { success: false, error: data.error || 'Unbekannter Fehler' }
  } catch (err) {
    return { success: false, error: (err as Error).message }
  }
}

type OAuthService = 'gmail' | 'calendar' | 'unknown'

function detectService(state: string | null): { service: OAuthService; userId: string } {
  if (!state) return { service: 'unknown', userId: '' }
  
  if (state.startsWith('gmail_')) {
    return { service: 'gmail', userId: state.slice(6) }
  }
  if (state.startsWith('gcal_')) {
    return { service: 'calendar', userId: state.slice(5) }
  }
  
  // Fallback: Prüfe localStorage welcher Flow zuletzt gestartet wurde
  const gmailState = localStorage.getItem('gmail_oauth_state')
  const gcalState = localStorage.getItem('gcal_oauth_state')
  
  if (gmailState && state.includes(gmailState)) {
    return { service: 'gmail', userId: gmailState }
  }
  if (gcalState && state.includes(gcalState)) {
    return { service: 'calendar', userId: gcalState }
  }
  
  // Letzter Fallback: Prüfe welcher State gespeichert ist
  if (gmailState) return { service: 'gmail', userId: gmailState }
  if (gcalState) return { service: 'calendar', userId: gcalState }
  
  return { service: 'unknown', userId: state }
}

export default function GoogleCalendarCallback() {
  const [, navigate] = useLocation()
  const { handleCallback: handleCalendarCallback } = useGoogleCalendar()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [service, setService] = useState<OAuthService>('unknown')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const error = params.get('error')
    const errorDescription = params.get('error_description')

    // Fehler von Google
    if (error) {
      setStatus('error')
      if (error === 'access_denied') {
        setMessage('Zugriff verweigert. Der Nutzer hat die Berechtigung abgelehnt.')
      } else {
        setMessage(`Google-Fehler: ${error}${errorDescription ? ` — ${errorDescription}` : ''}`)
      }
      setTimeout(() => navigate('/einstellungen/integrationen'), 4000)
      return
    }

    if (!code) {
      setStatus('error')
      setMessage('Kein Autorisierungscode erhalten. Der OAuth-Flow wurde unterbrochen.')
      setTimeout(() => navigate('/einstellungen/integrationen'), 4000)
      return
    }

    // Service erkennen basierend auf State-Prefix
    const { service: detectedService, userId } = detectService(state)
    setService(detectedService)

    if (detectedService === 'gmail') {
      // Gmail OAuth Exchange
      const gmailUserId = userId || localStorage.getItem('gmail_oauth_state') || 
        ('user_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9))
      
      // User-ID für zukünftige Nutzung speichern
      localStorage.setItem('gmail_user_id', gmailUserId)
      
      exchangeGmailCode(code, gmailUserId).then(result => {
        if (result.success) {
          setStatus('success')
          setEmail(result.email || '')
          setMessage(`Gmail erfolgreich verbunden: ${result.email}`)
          const returnPath = localStorage.getItem('gmail_return_path') || '/kommunikation'
          setTimeout(() => navigate(returnPath), 2000)
        } else {
          setStatus('error')
          setMessage(result.error || 'Gmail-Verbindung fehlgeschlagen.')
        }
      })
    } else if (detectedService === 'calendar') {
      // Calendar OAuth Exchange
      handleCalendarCallback(code, state || '').then((result) => {
        if (result === true) {
          setStatus('success')
          setMessage('Google Calendar erfolgreich verbunden!')
          setTimeout(() => navigate('/'), 2000)
        } else {
          setStatus('error')
          setMessage(typeof result === 'string' ? result : 'Calendar-Verbindung fehlgeschlagen.')
        }
      })
    } else {
      // Unbekannter Service — versuche beide State-Stores
      setStatus('error')
      setMessage('Konnte den OAuth-Dienst nicht erkennen. Bitte versuchen Sie es erneut über die Einstellungen.')
      setTimeout(() => navigate('/einstellungen/integrationen'), 4000)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isGmail = service === 'gmail'
  const serviceLabel = isGmail ? 'Gmail' : 'Google Calendar'
  const ServiceIcon = isGmail ? Mail : Calendar
  const accentColor = isGmail ? '#EA4335' : '#C41E3A'

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Service Icon */}
        <div className="flex justify-center mb-6">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            {status === 'loading' ? (
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
            ) : status === 'success' ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <XCircle className="w-8 h-8 text-red-500" />
            )}
          </div>
        </div>

        {/* Service Badge */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <ServiceIcon className="w-5 h-5" style={{ color: accentColor }} />
          <span className="text-sm font-medium text-gray-500">{serviceLabel} Integration</span>
        </div>

        {/* Title */}
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {status === 'loading' && `${serviceLabel} wird verbunden...`}
          {status === 'success' && `${serviceLabel} verbunden!`}
          {status === 'error' && 'Verbindung fehlgeschlagen'}
        </h1>

        {/* Message */}
        <p className="text-gray-600 text-sm mb-6">
          {status === 'loading' && 'Bitte warten, der Autorisierungscode wird verarbeitet...'}
          {status === 'success' && (
            <>
              {email && <span className="font-medium text-green-600">{email}</span>}
              {email && <br />}
              <span className="text-gray-500">Sie werden weitergeleitet...</span>
            </>
          )}
          {status === 'error' && message}
        </p>

        {/* Action buttons bei Fehler */}
        {status === 'error' && (
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/einstellungen/integrationen')}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Zu den Einstellungen
            </button>
            <button
              onClick={() => navigate(isGmail ? '/kommunikation' : '/')}
              className="px-4 py-2 text-sm text-white rounded-lg transition-colors"
              style={{ backgroundColor: accentColor }}
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="text-xs text-gray-400">
            Weiterleitung in 2 Sekunden...
          </div>
        )}

        {/* Debug Info bei unbekanntem Service */}
        {service === 'unknown' && status === 'error' && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-left">
            <p className="text-xs text-gray-400 font-mono">
              Tipp: Starten Sie den Verbindungsvorgang erneut über Einstellungen &rarr; Integrationen
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
